import type { LocationFilter as LocationFilterValue, WorkMode } from "@resume-cloudflare/shared";
import { MultiSelect, type MultiSelectOption } from "../ui/MultiSelect";

// Sidebar filter group that drives the structured-location params on
// /api/jobs (mode[], eligible_in, max_in_office_days, max_travel_pct,
// requires_no_visa_sponsorship, requires_relocation_offered). Controlled —
// the parent owns persistence (see useLocalStorageState in Jobs.tsx).
//
// Today the "Eligible where I live" toggle binds eligible_in to the
// literal "me" — the worker resolves that against the user's primary
// country. A 2-letter ISO override would need a country picker, which
// isn't on this surface yet.

const MODE_OPTIONS: MultiSelectOption<WorkMode>[] = [
  { value: "remote", label: "Remote" },
  { value: "hybrid", label: "Hybrid" },
  { value: "onsite", label: "Onsite" },
  { value: "unspecified", label: "Unspecified" },
];

export const DEFAULT_LOCATION_FILTER: LocationFilterValue = {
  eligible_in: "me",
};

export function LocationFilter({
  value,
  onChange,
}: {
  value: LocationFilterValue;
  onChange: (next: LocationFilterValue) => void;
}) {
  function patch(p: Partial<LocationFilterValue>) {
    onChange({ ...value, ...p });
  }

  const eligibleEnabled = value.eligible_in === "me";

  return (
    <div className="space-y-3">
      <Field label="Mode">
        <MultiSelect<WorkMode>
          options={MODE_OPTIONS}
          value={value.mode ?? []}
          onChange={(modes) => patch({ mode: modes.length ? modes : undefined })}
          placeholder="Any mode"
        />
      </Field>

      <label className="flex cursor-pointer items-center justify-between gap-2 rounded border border-zinc-800 bg-zinc-950 px-2.5 py-1.5">
        <span className="text-xs text-zinc-200">Eligible where I live</span>
        <input
          type="checkbox"
          checked={eligibleEnabled}
          onChange={(e) =>
            patch({ eligible_in: e.target.checked ? "me" : undefined })
          }
          className="h-4 w-4 cursor-pointer accent-accent-500"
        />
      </label>

      <details className="rounded border border-zinc-800 bg-zinc-950 px-2.5 py-1.5 text-xs text-zinc-300 [&_summary]:cursor-pointer">
        <summary className="text-[11px] uppercase tracking-wider text-zinc-500 outline-none focus-visible:text-zinc-300">
          Advanced
        </summary>
        <div className="mt-3 space-y-3">
          <Field label="Max in-office days">
            <input
              type="number"
              min={0}
              max={7}
              value={value.max_in_office_days ?? ""}
              placeholder="0–7"
              className={fieldCls}
              onChange={(e) =>
                patch({
                  max_in_office_days:
                    e.target.value === "" ? undefined : Number(e.target.value),
                })
              }
            />
          </Field>

          <Field label="Max travel %">
            <input
              type="number"
              min={0}
              max={100}
              value={value.max_travel_pct ?? ""}
              placeholder="0–100"
              className={fieldCls}
              onChange={(e) =>
                patch({
                  max_travel_pct:
                    e.target.value === "" ? undefined : Number(e.target.value),
                })
              }
            />
          </Field>

          <label className="flex cursor-pointer items-center gap-2">
            <input
              type="checkbox"
              checked={!!value.requires_no_visa_sponsorship}
              onChange={(e) =>
                patch({
                  requires_no_visa_sponsorship: e.target.checked || undefined,
                })
              }
              className="h-4 w-4 cursor-pointer accent-accent-500"
            />
            <span>No visa sponsorship needed</span>
          </label>

          <label className="flex cursor-pointer items-center gap-2">
            <input
              type="checkbox"
              checked={!!value.requires_relocation_offered}
              onChange={(e) =>
                patch({
                  requires_relocation_offered: e.target.checked || undefined,
                })
              }
              className="h-4 w-4 cursor-pointer accent-accent-500"
            />
            <span>Relocation offered</span>
          </label>
        </div>
      </details>
    </div>
  );
}

function Field({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}) {
  return (
    <label className="block">
      <span className="block text-[11px] uppercase tracking-wider text-zinc-500">
        {label}
      </span>
      <div className="mt-1">{children}</div>
    </label>
  );
}

const fieldCls =
  "w-full rounded border border-zinc-800 bg-zinc-950 px-2 py-1 text-sm text-zinc-100 outline-none focus:border-zinc-600";
